<template>
  <ak-sidebar-layout overflow-hidden>
    <template #sidebar>
      <ak-folders-sidebar
        ref="folders"
        v-model:active-folder="activeFolder"
        :active-account="activeAccount"
        :active-agency="activeAgency"
        :show-sort="true"
      />
    </template>
    <template #body>
      <div class="relative h-full">
        <ak-basic-container class="flex flex--column">
          <ak-box class="pb-none">
            <h4 class="color-grey mb-small">{{ activeTemplates }}</h4>
            <ak-form class="max-width-350 mb-small" @submit="runSearch">
              <ak-input
                v-model="searchFilter"
                type="search"
                icon="search"
                @right-icon-click="runSearch"
              />
            </ak-form>
          </ak-box>
          <ak-box class="flex px-none">
            <ak-email-stats-box
              text="Total Sent"
              popover-text="Total number of emails sent."
              :display-value="stats.count"
              icon="fa-envelope-circle-check"
              even-width
            />
            <ak-email-stats-box
              text="Total Unique Opens"
              popover-text="Total number of times the email was opened by different receivers."
              :display-value="stats.opens"
              icon="fa-envelope-open"
              even-width
            />
            <ak-email-stats-box
              text="Total Unique Clicks"
              popover-text="Total number of times the email was clicked in by different receivers."
              :display-value="stats.clicks"
              icon="fa-bullseye-pointer"
              even-width
            />
            <!-- <ak-email-stats-box
              text="Open Rate"
              popover-text="Percentage of recipients (successful deliveries) who opened the email."
              :display-value="stats.openRate"
              icon="fa-envelope-open"
              even-width
            /> -->
            <!-- <ak-email-stats-box
              text="Click Rate"
              popover-text="Percentage of recipients (successful deliveries) who clicked  in the email"
              :display-value="stats.clickRate"
              icon="fa-chart-line"
              even-width
            /> -->
            <ak-email-stats-box
              text="Click-Through Rate"
              popover-text="Percentage of total clicks within the email in relation to how many emails were delivered."
              :display-value="stats.clickTroughRate"
              icon="fa-chart-line"
              even-width
            />
            <ak-email-stats-box
              text="Total Unsubscribes"
              popover-text="Total number of times a recipient successfully unsubscribed."
              :display-value="stats.unsubscribed"
              icon="fa-user-xmark"
              even-width
            />
          </ak-box>
          <div class="bg-white flex__item">
            <ak-table
              v-model:current-page="currentPage"
              v-model:data="templates"
              v-model:loading="loading"
              class="one-whole h-full mb-huge overflow-x-y text-nowrap"
              :default-sort="defaultSort"
              :default-sort-direction="defaultSortDirection"
              :per-page="perPage"
              :total="total"
              :show-rows="shouldDisplayTableRows"
              spacing="small"
              backend-pagination
              backend-sorting
              sortable
              inset
              hoverable
              background
              paginated
              @page="onPageChange"
              @sort="onSort"
            >
              <template #empty>
                <ak-empty-state
                  class="pt"
                  icon="mail"
                  title="Currently there aren't any email templates"
                >
                  <ak-button tag="router-link" :to="{ name: 'emailsNew' }">
                    Create Email Response
                  </ak-button>
                </ak-empty-state>
              </template>

              <template #default="{ row }">
                <ak-table-column
                  field="title"
                  label="Title"
                  sortable
                  heading-min-width="300px"
                >
                  <router-link
                    :to="{ name: 'emailsEdit', params: { id: row._id } }"
                    class="color-link-hover block"
                  >
                    <strong>{{ row.title }}</strong>
                    <div class="text-small color-text">
                      {{ row.subject }}
                    </div>
                  </router-link>
                </ak-table-column>
                <ak-table-column
                  field="stats.count"
                  label="Sent"
                  numeric
                  sortable
                  header-spacing
                >
                  <div class="px-small">
                    {{ row.stats.count }}
                  </div></ak-table-column
                >
                <ak-table-column
                  field="stats.opens"
                  label="Unique Opens"
                  numeric
                  sortable
                  header-spacing
                >
                  <div class="px-small">
                    {{ row.stats.opens }}
                  </div></ak-table-column
                >
                <ak-table-column
                  field="stats.clicks"
                  label="Unique Clicks"
                  numeric
                  sortable
                  header-spacing
                >
                  <div class="px-small">
                    {{ row.stats.clicks }}
                  </div></ak-table-column
                >
                <ak-table-column
                  label="Click-Through Rate"
                  numeric
                  header-spacing
                >
                  <div class="px-small">
                    {{ getPercent(row.stats.clicks, row.stats.opens) }}
                  </div>
                </ak-table-column>
                <ak-table-column label="Open Rate" numeric header-spacing>
                  <div v-if="canShowField(row)" class="px-small">
                    {{ getPercent(row.stats.opens, row.stats.delivered) }}
                  </div>
                  <ak-email-list-popover v-else />
                </ak-table-column>
                <ak-table-column label="Click Rate" numeric header-spacing>
                  <div v-if="canShowField(row)" class="px-small">
                    {{ getPercent(row.stats.clicks, row.stats.delivered) }}
                  </div>
                  <ak-email-list-popover v-else />
                </ak-table-column>
                <ak-table-column
                  label="Delivered"
                  numeric
                  field="stats.delivered"
                  sortable
                  header-spacing
                >
                  <div v-if="canShowField(row)" class="px-small">
                    {{ row.stats.delivered || 0 }}
                  </div>
                  <ak-email-list-popover v-else />
                </ak-table-column>
                <ak-table-column
                  label="Bounced"
                  numeric
                  field="stats.bounced"
                  sortable
                  header-spacing
                >
                  <div v-if="canShowField(row)" class="px-small">
                    {{ row.stats.bounced || 0 }}
                  </div>
                  <ak-email-list-popover v-else />
                </ak-table-column>
                <ak-table-column
                  field="stats.unsubscribed"
                  label="Unsubscribes"
                  numeric
                  sortable
                  header-spacing
                >
                  <div class="px-small">
                    {{ row.stats.unsubscribed }}
                  </div></ak-table-column
                >
                <ak-table-column field="createdOn" label="Created" sortable>
                  {{ fromNow(row.createdOn) }}
                </ak-table-column>
                <ak-table-column field="updatedOn" label="Updated" sortable>
                  {{ fromNow(row.updatedOn) }}
                </ak-table-column>
                <ak-table-column right mini>
                  <ak-dropdown position-x="right">
                    <template #trigger>
                      <ak-button size="small" circle color="transparent">
                        <ak-icon type="more-vertical" />
                      </ak-button>
                    </template>
                    <template #default>
                      <router-link
                        :to="{
                          name: 'emailsEdit',
                          params: { id: row._id },
                        }"
                      >
                        <ak-dropdown-item> Edit </ak-dropdown-item>
                      </router-link>
                      <ak-dropdown-item @click="openCloneModal(row)">
                        <a>Clone</a>
                      </ak-dropdown-item>
                      <ak-dropdown-item @click="deleteTemplate(row)">
                        <a>Delete</a>
                      </ak-dropdown-item>
                    </template>
                  </ak-dropdown>
                </ak-table-column>
              </template>
            </ak-table>
          </div>
        </ak-basic-container>

        <ak-fab
          tag="router-link"
          :to="{ name: 'emailsNew' }"
          icon="plus"
          position-x="center"
          position-y="bottom"
        />
      </div>

      <ak-modal
        v-model:active="modalActive"
        outer-scroll
        size="small"
        :submit-form="cloneTemplate"
      >
        <ak-modal-body v-if="activeTemplate">
          <ak-modal-title>Clone Template</ak-modal-title>
          <ak-field label="Enter a new template name">
            <ak-input v-model="activeTemplate.title" requird />
          </ak-field>
          <ak-field label="Folder to clone to">
            <ak-select
              v-model="activeTemplate.folder"
              :options="folders.folders"
            />
          </ak-field>
        </ak-modal-body>
        <ak-modal-footer>
          <ak-modal-button type="submit">Clone Template</ak-modal-button>
        </ak-modal-footer>
      </ak-modal>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { computed, ref, watch, onMounted, defineComponent } from 'vue'
import BasicContainer from '../containers/BasicContainer.vue'
import Folders from '../folders/Folders.vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Fab from '@akerolabs/akero-vue/src/components/fab/Fab.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import EmailListPopover from './EmailListPopover.vue'
import { fromNow } from '@akerolabs/akero-vue/src/filters/date'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { setNameFromFolders } from '../../utils/helpers.js'
import StatsBox from './EmailStatsBox.vue'
const dateCheck = new Date('2023-02-15T16:00:00.000Z')

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-templates-list></ak-email-templates-list>
   */
  name: 'AkEmailTemplatesList',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [BasicContainer.name]: BasicContainer,
    [SidebarLayout.name]: SidebarLayout,
    [Folders.name]: Folders,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Input.name]: Input,
    [Form.name]: Form,
    [Box.name]: Box,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Modal.name]: Modal,
    [ModalTitle.name]: ModalTitle,
    [ModalButton.name]: ModalButton,
    [Field.name]: Field,
    [Input.name]: Input,
    [Select.name]: Select,
    [Button.name]: Button,
    [Fab.name]: Fab,
    [Icon.name]: Icon,
    [EmptyState.name]: EmptyState,
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
    [StatsBox.name]: StatsBox,
    [EmailListPopover.name]: EmailListPopover,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Function}
     * @description Checks if a feature is available for the current account
     */
    featureEnabled: {
      default: undefined,
      type: Function,
    },
  },

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const activeFolder = ref('all')
    const activeTemplates = ref(setNameFromFolders('Email', activeFolder.value))
    const defaultSortDirection = ref('desc')
    const defaultSort = ref('createdOn')
    const currentPage = ref(1)
    const perPage = ref(20)
    const total = ref(0)
    const searchFilter = ref('')
    const templates = ref([])
    const loading = ref(true)
    const modalActive = ref(false)
    const activeTemplate = ref(null)
    const folders = ref(null)
    const stats = ref({
      clicks: 0,
      count: 0,
      opens: 0,
      unsubscribed: 0,
      openRate: '0.0%',
      clickRate: '0.0%',
      clickTroughRate: '0.0%',
    })

    const shouldDisplayTableRows = computed(() => {
      return templates.value.length >= 1
    })

    /**
     * @returns {Object}
     * @description Returns the settings for the search part of the request
     * object to send to the akero API.
     */
    const searchOptions = computed(() => {
      const result = {}
      if (searchFilter.value.length > 0) {
        const searchLower = searchFilter.value.toLowerCase()
        result.titleLower = JSON.stringify({ $regex: searchLower })
      }
      return result
    })

    /**
     * @returns {Object}
     * @description Returns the settings for the sorting order in the
     * query to send to the API
     */
    const sortOptions = computed(() => {
      return {
        sort: `${defaultSortDirection.value === 'desc' ? '-' : ''}${
          defaultSort.value
        }`,
      }
    })

    /**
     * @returns {Object}
     * @description Returns the folder part of the query for filtering
     * the email templates.
     */
    const folderOptions = computed(() => {
      const result = {}
      if (activeFolder.value !== 'all') {
        result.folder = activeFolder.value
      }
      return result
    })

    /**
     * @returns {Object}
     * @description Returns the request for fetching the email templates
     * from the API.
     */
    const templatesRequest = computed(() => {
      return {
        params: {
          ...sortOptions.value,
          ...searchOptions.value,
          ...folderOptions.value,
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
          deleted: false,
          per_page: perPage.value,
          page: currentPage.value,
          type: 'email',
        },
      }
    })

    /**
     * @param {Number} page
     * @description Callback for the on page change event for the table.
     */
    const onPageChange = async (page) => {
      currentPage.value = page
      await fetchTemplates()
    }

    /**
     * @param {String} sort
     * @param {String} direction
     * @description Callback for the sort event for the table.
     */
    const onSort = async (sort, direction) => {
      defaultSortDirection.value = direction
      defaultSort.value = sort
      await fetchTemplates()
    }

    /**
     * @returns {Promise}
     * @description Returns a promise to fetch the email temlates for
     * the active account.
     */
    const fetchTemplates = async () => {
      loading.value = true
      let templatesData = []
      let totalNum = 0
      let page = 1

      const displayError = (type = 'templates', ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load email ${type}.`,
          type: 'danger',
        })
      }

      try {
        const { data } = await request.get('/templates', templatesRequest.value)
        templatesData = data.data
        totalNum = data.meta.pagination.total
        page = data.meta.pagination.current
      } catch (ex) {
        templatesData = []
        displayError('templates', ex)
      }

      templates.value = templatesData
      currentPage.value = page
      total.value = totalNum
      loading.value = false
    }

    /**
     * @param {Object} template
     * @description Opens the clone template modal
     */
    const openCloneModal = (template) => {
      activeTemplate.value = { ...template, title: '' }
      modalActive.value = true
    }

    /**
     * @param {Object} template
     * @description Sends a request to the API to create the cloned
     * template
     */
    const cloneTemplate = () => {
      const template = { ...activeTemplate.value }

      template.cloned = true
      template.clonedBy = props.userProfile._id
      template.clonedOn = Date.now()
      template.clonedFrom = template._id

      delete template._id
      delete template.id
      delete template.__v
      delete template.createdBy
      delete template.createdOn
      delete template.updatedBy
      delete template.updatedOn
      delete template.stats

      template.deleted = false
      template.expires = null

      request
        .post('/templates', template)
        .then(fetchTemplates)
        .then(() => {
          ToastProgrammatic.open({
            message: 'Template has been cloned.',
            type: 'success',
          })
        })
        .catch(() => {
          ToastProgrammatic.open({
            message: 'Error tempate could not be cloned.',
            type: 'danger',
          })
        })
    }

    /**
     * @param {Object} template
     * @description Removes the requested template
     */
    const removeTemplate = (template) => {
      request
        .delete(`/templates/${template._id}`)
        .then(fetchTemplates)
        .then(() => {
          ToastProgrammatic.open({
            message: 'Template has been deleted.',
            type: 'success',
          })
        })
        .catch(() => {
          ToastProgrammatic.open({
            message: 'Error folder could not be deleted.',
            type: 'danger',
          })
        })
    }

    /**
     * @param {Object} template
     * @description Triggers a dialog to confirm if you want to
     * delete the requested template.
     */
    const deleteTemplate = (template) => {
      DialogProgrammatic.prompt({
        title: 'Deleting template',
        message: `Type "${template.title}" to delete this template`,
        inputAttrs: {
          pattern: template.title,
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        confirmText: 'Delete Template',
        cancelText: 'No, keep',
        onConfirm: () => removeTemplate(template),
      })
    }

    const canShowField = (template) => {
      const createdOn = new Date(template.createdOn)
      if (createdOn < dateCheck) {
        return false
      }
      return true
    }

    const getPercent = (a, b) => {
      if (!a || !b) return '0%'
      const result = parseFloat((a / b) * 100).toFixed(1)
      if (isNaN(result)) return '0%'
      if (result === 0) return '0%'
      return `${result}%`
    }

    const fetchStats = async () => {
      loading.value = true
      try {
        const { data } = await request(
          '/templates/stats',
          templatesRequest.value,
        )
        stats.value = data.data
      } catch (ex) {
        stats.value = {
          clicks: 0,
          count: 0,
          opens: 0,
          unsubscribed: 0,
          openRate: '0.0%',
          clickRate: '0.0%',
          clickTroughRate: '0.0%',
        }
      }
      loading.value = false
    }

    const runSearch = async () => {
      await fetchStats()
      await fetchTemplates()
    }

    onMounted(async () => {
      await fetchStats()
      await fetchTemplates()
    })

    watch(activeFolder, async () => {
      await fetchStats()
      await fetchTemplates()
      activeTemplates.value = setNameFromFolders('Email', activeFolder.value)
    })

    watch(modalActive, (value) => {
      if (value === false) {
        activeTemplate.value = null
      }
    })

    return {
      activeTemplates,
      activeFolder,
      defaultSortDirection,
      defaultSort,
      currentPage,
      perPage,
      total,
      searchFilter,
      templates,
      loading,
      modalActive,
      activeTemplate,
      shouldDisplayTableRows,
      searchOptions,
      sortOptions,
      folderOptions,
      templatesRequest,
      stats,
      fromNow,
      deleteTemplate,
      removeTemplate,
      fetchTemplates,
      cloneTemplate,
      openCloneModal,
      onSort,
      onPageChange,
      folders,
      getPercent,
      runSearch,
      canShowField,
    }
  },
})
</script>
